import { api, INCOME_TYPE_LABELS, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { NewsPopup } from '../components/NewsPopup';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../hooks/useSession';
import { formatLds, formatNumber, formatUsd, WITHDRAWAL_LABEL } from '../lib/format';
import { Alert, Button, Card, Input, ProgressBar, Spinner, StatCard, StatCards } from '../ui';

function ReferralLinkCard({ userCode }: { userCode: string }) {
  const [copied, setCopied] = useState(false);
  // 2026-09-11: was hardcoded to app.alphaone.group — wrong on any other
  // build (e.g. the lds1 subdomain), where a shared referral link must point
  // back to lds1.alphaone.group, not the main site. window.location.origin
  // is correct on every deployment with zero per-build configuration.
  const link = `${window.location.origin}/register?ref=${userCode}`;
  const shareMessage = `Join Alpha One using my referral link: ${link}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card>
      <h3 className="card-title">Your Referral Link</h3>
      <p className="text-muted small">Share this link — anyone who signs up through it is placed under you.</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <Input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
        </div>
        <Button
          type="button"
          variant="gold"
          onClick={copyLink}
          leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          as="a"
          href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="outline-gold"
          leftIcon={<MessageCircle size={16} />}
        >
          Share on WhatsApp
        </Button>
      </div>
    </Card>
  );
}

export function Dashboard() {
  const { user } = useSession();

  const dashboard = useQuery({
    queryKey: queryKeys.dashboard('user'),
    queryFn: () => api.income.summary().then((r) => r.data),
  });

  const wallet = useQuery({
    queryKey: queryKeys.wallet(),
    queryFn: () => api.wallet.balances().then((r) => r.data),
  });

  const team = useQuery({
    queryKey: queryKeys.team('stats', null),
    queryFn: () => api.team.stats().then((r) => r.data),
  });

  const ranks = useQuery({
    queryKey: queryKeys.ranks(),
    queryFn: () => api.ranks.current().then((r) => r.data),
  });

  if (dashboard.isLoading) return <Spinner />;

  const nextRank = ranks.data?.next;

  return (
    <>
      <NewsPopup />

      <PageHeader title="Dashboard" description="Your Alpha One overview" />

      {!wallet.data ? null : Number(wallet.data.income_balance) < 25 ? (
        <Alert tone="info">Minimum withdrawal is $25 in {WITHDRAWAL_LABEL}. Withdrawals run 24×7 with no admin fee.</Alert>
      ) : null}

      {user?.user_code ? <ReferralLinkCard userCode={user.user_code} /> : null}

      <StatCards>
        <StatCard label="Activation Wallet" value={formatUsd(wallet.data?.activation_balance)} />
        <StatCard label="Income Wallet" value={formatLds(wallet.data?.income_balance_lds)} />
        <StatCard label="Total Earned" value={formatLds(wallet.data?.total_earned_lds)} />
        <StatCard label="Total Withdrawn" value={formatLds(wallet.data?.total_withdrawn_lds)} />
        <StatCard
          label="Direct Team"
          value={formatNumber(team.data?.direct_count)}
          sub={`${formatNumber(team.data?.direct_active)} active`}
        />
        <StatCard
          label="Total Team"
          value={formatNumber(team.data?.total_team)}
          sub={`${formatNumber(team.data?.total_active)} active`}
        />
        {/* 2026-08-27: no separate "Virtual Power" box, per client — Left/Right
            Leg already equal real + virtual combined (BinaryCounterService),
            so a distinct box here reads as double-counting even though it
            isn't. Keep left/right as the single, merged figures. */}
        <StatCard label="Left Leg" value={formatNumber(team.data?.left_count)} />
        <StatCard label="Right Leg" value={formatNumber(team.data?.right_count)} />
      </StatCards>

      <Card>
        <h3 className="card-title">Income by type</h3>
        <ul className="income-breakdown">
          {Object.entries(dashboard.data?.totals_lds ?? {}).map(([type, amount]) => (
            <li key={type}>
              <span>{INCOME_TYPE_LABELS[type as keyof typeof INCOME_TYPE_LABELS] ?? type}</span>
              <strong className="text-gold">{formatLds(amount)}</strong>
            </li>
          ))}
        </ul>
      </Card>

      {nextRank ? (
        <Card>
          <h3 className="card-title">Next rank — {nextRank.name}</h3>
          <p className="text-muted small">{nextRank.requirement_label}</p>
          <ProgressBar
            value={nextRank.progress_current}
            max={nextRank.progress_required}
            label={`${formatNumber(nextRank.progress_current)} / ${formatNumber(nextRank.progress_required)}`}
          />
        </Card>
      ) : null}

      <Card>
        <h3 className="card-title">Earnings — last 30 days</h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={[]}>
              <defs>
                <linearGradient id="goldFade" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#c9a84c" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#c9a84c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1c1c1c" vertical={false} />
              <XAxis dataKey="date" stroke="#888" fontSize={12} tickLine={false} />
              <YAxis stroke="#888" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: '#161616', border: '1px solid #2a2a2a', borderRadius: 8 }} />
              <Area type="monotone" dataKey="amount" stroke="#c9a84c" fill="url(#goldFade)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}
